import React from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can, canAll } from "@src/security/permissions";

import styles from "./__BackofficeUserProfile.module.styl";
import RolePermissionSelector, {
  permissionNames,
  selectedPermissionNames,
  samePermissionSelection
} from "@src/users/RolePermissionSelector";

const QalaaUserProfile = ({
  gui,
  userId
}) => {
  const [profile, setProfile] =
    React.useState(null);

  const [selectedPermissions, setSelectedPermissions] =
    React.useState(new Set());

  const [initialPermissions, setInitialPermissions] =
    React.useState(new Set());

  const [loading, setLoading] =
    React.useState(true);

  const [saving, setSaving] =
    React.useState(false);

  const [error, setError] =
    React.useState("");

  const [saved, setSaved] =
    React.useState(false);

  const canManageAccess =
    can(gui, "MANAGE_BACKOFFICE_USER_ACCESS");

  const canReturnToList =
    canAll(gui, [
      "SEARCH_BACKOFFICE_USER",
      "COUNT_BACKOFFICE_USER"
    ]);

  const applyProfile = React.useCallback(
    (accessProfile) => {
      const selected =
        selectedPermissionNames(
          accessProfile
        );

      setProfile(accessProfile);
      setSelectedPermissions(selected);
      setInitialPermissions(
        new Set(selected)
      );
    },
    []
  );

  const load = React.useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const accessProfile =
          await rest.request(
            getServiceUri()
              + `__backoffice-role/${userId}/access`,
            "GET",
            null
          );

        applyProfile(accessProfile);

      } catch (requestError) {
        console.error(
          "Failed to load the user profile",
          requestError
        );

        setError(
          t("user.profileCouldNotBeLoaded")
        );

      } finally {
        setLoading(false);
      }
    },
    [applyProfile, userId]
  );

  React.useEffect(() => {
    load();
  }, [load]);

  const toggleRoleSet = (
    roleSet
  ) => {
    if (!canManageAccess) {
      return;
    }

    const names =
      permissionNames(roleSet);

    setSaved(false);

    setSelectedPermissions(
      (current) => {
        const next =
          new Set(current);

        if (names.includes("ALL")) {
          if (next.has("ALL")) {
            next.delete("ALL");
          } else {
            next.clear();
            next.add("ALL");
          }

          return next;
        }

        next.delete("ALL");

        const fullySelected =
          names.length > 0
          && names.every(
            (name) => next.has(name)
          );

        for (const name of names) {
          if (fullySelected) {
            next.delete(name);
          } else {
            next.add(name);
          }
        }

        return next;
      }
    );
  };

  const togglePermission = (
    permissionName
  ) => {
    if (!canManageAccess) {
      return;
    }

    setSaved(false);

    setSelectedPermissions(
      (current) => {
        const next =
          new Set(current);

        if (permissionName === "ALL") {
          if (next.has("ALL")) {
            next.delete("ALL");
          } else {
            next.clear();
            next.add("ALL");
          }

          return next;
        }

        next.delete("ALL");

        if (next.has(permissionName)) {
          next.delete(permissionName);
        } else {
          next.add(permissionName);
        }

        return next;
      }
    );
  };

  const save = async () => {
    if (!canManageAccess || saving) {
      return;
    }

    setSaving(true);
    setSaved(false);
    setError("");

    try {
      const accessProfile =
        await rest.request(
          getServiceUri()
            + `__backoffice-role/${userId}/access`,
          "PUT",
          {
            permissions:
              Array.from(
                selectedPermissions
              )
          }
        );

      applyProfile(accessProfile);
      setSaved(true);

    } catch (requestError) {
      console.error(
        "Failed to save Qalaa user permissions",
        requestError
      );

      setError(
        t("user.roleAssignmentsCouldNotBeSaved")
      );

    } finally {
      setSaving(false);
    }
  };

  if (
    !Number.isFinite(Number(userId))
    || Number(userId) <= 0
  ) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          {t("user.invalidQalaaUserId")}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <CircularProgress size={30} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={styles.page}>
        {canReturnToList ? (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              gui.goTo("/__backoffice-user")
            }
          >
            {t("user.backToUsers")}
          </Button>
        ) : null}

        <div className={styles.error}>
          {error || t("user.userNotFound")}
        </div>
      </div>
    );
  }

  const user = profile.user;

  const dirty =
    !samePermissionSelection(
      selectedPermissions,
      initialPermissions
    );

  const deleted =
    user?.deleted === true;

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        {canReturnToList ? (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              gui.goTo("/__backoffice-user")
            }
          >
            {t("user.backToUsers")}
          </Button>
        ) : null}
      </div>

      <header className={styles.header}>
        <div>
          <div className={styles.eyebrow}>
            {t("user.qalaaUser")}
          </div>

          <h1 className={styles.title}>
            {user?.username
              || `${t("common.user")} ${user?.id}`}
          </h1>

          <div className={styles.metadata}>
            <span>ID {user?.id}</span>

            <span
              className={
                deleted
                  ? `${styles.status} ${styles.statusDeleted}`
                  : `${styles.status} ${styles.statusActive}`
              }
            >
              {deleted
                ? t("common.deleted")
                : t("common.active")}
            </span>
          </div>
        </div>
      </header>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>
              {t("user.rolesAndPermissions")}
            </h2>

            <p className={styles.panelDescription}>
              {t("user.selectFunctionalAccessGranular")}
            </p>
          </div>

          {canManageAccess ? (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving || !dirty}
              onClick={save}
            >
              {saving
                ? t("common.saving")
                : t("user.savePermissions")}
            </Button>
          ) : null}
        </div>

        <RolePermissionSelector
          roleSets={profile.roleSets || []}
          selectedPermissions={selectedPermissions}
          saving={saving || !canManageAccess}
          onToggleRole={toggleRoleSet}
          onTogglePermission={togglePermission}
        />

        {error ? (
          <div className={styles.error}>
            {error}
          </div>
        ) : null}

        {saved ? (
          <div className={styles.success}>
            {t("user.permissionAssignmentsSaved")}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export const displayBackofficeUserProfile = (
  gui,
  userId
) => {
  const id = Number(userId);

  return {
    view: () => (
      <QalaaUserProfile
        gui={gui}
        userId={id}
      />
    )
  };
};

export default displayBackofficeUserProfile;
